import React, { useState, useEffect } from "react";
import { Button, Collapse, IconButton, MenuItem, Select, FormControl } from "@material-ui/core";
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from "@material-ui/icons";
import GameRulesDialog from "../game/GameRulesDialog";
import PlayerAvatar from "../ui/PlayerAvatar";
import LobbyPlayerSlot from "../ui/LobbyPlayerSlot";
import CopyButton from "../ui/CopyButton";
import NativeShareButton from "../ui/NativeShareButton";
import CustomizeAvatarDialog from "../ui/CustomizeAvatarDialog";
import LobbyChat from "../ui/LobbyChat";
import GameOptions from "../ui/GameOptions";
import { BOT_PERSONALITIES, getPersonalityValue, getPersonalityLabel } from "../../utils/botPersonalityUtils";

const Lobby = ({
  room,
  name,
  playerCount,
  playerNames,
  playerColors,
  playerEmojis,
  updates,
  playerReady = {},
  isReady = false,
  onToggleReady,
  onStartGame,
  onLeaveRoom,
  onAddBot,
  onRemoveBot,
  onChangePlayerColor,
  onChangePlayerEmoji,
  onRandomizePlayerColor,
  onRandomizePlayerEmoji,
  onRandomizeAll,
  availableColors = [],
  availableEmojis = [],
  countdown = null,
  socket = null,
  chatMessages = [],
  isFirstPlayer = false,
  botPersonalities = {},
  onChangeBotPersonality = null,
  gameOptions = {},
  onUpdateGameOption = null
}) => {
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [customizeDialogOpen, setCustomizeDialogOpen] = useState(false);
  const [activityFeedExpanded, setActivityFeedExpanded] = useState(true);
  const [chatExpanded, setChatExpanded] = useState(true);
  const [editingBotPersonality, setEditingBotPersonality] = useState(null);
  const [addBotSlotIndex, setAddBotSlotIndex] = useState(null); // Track which slot is adding a bot

  // When entering the lobby view, ensure the page is scrolled to the top.
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, []);
  // Check if all players are ready
  // Filter out undefined/null values (used for slot positioning) before checking ready status
  const validPlayerNames = playerNames.filter(name => name !== undefined && name !== null);
  const allPlayersReady = validPlayerNames.length >= 2 && 
                          validPlayerNames.every(playerName => playerReady[playerName] === true);

  // Create 8 slots - fill with players, then empty slots
  // Handle sparse arrays (where some positions might be undefined)
  const MAX_SLOTS = 8;
  const slots = [];
  for (let i = 0; i < MAX_SLOTS; i++) {
    if (i < playerNames.length && playerNames[i] !== undefined && playerNames[i] !== null) {
      slots.push({ type: 'player', playerName: playerNames[i] });
    } else {
      slots.push({ type: 'empty', index: i });
    }
  }

  const handleRandomizeAll = () => {
    // Use server-side logic via onRandomizeAll callback
    if (onRandomizeAll) {
      onRandomizeAll();
    }
  };


  return (
    <div className="lobby-page">
      <div className="lobby-container">
        {/* Lobby Header */}
        <div className="lobby-header">
          <div className="lobby-title">
            <span className="lobby-icon">🎪</span>
            <h1>Game Lobby</h1>
          </div>
          <div className="room-badge">
            <span className="badge-label">Room Code:</span>
            <span className="badge-value">{room}</span>
            <CopyButton 
              text={`Hey! Come join me in room "${room}" for a game of Pump N Dump! 🎮\n\nA strategic card game where players buy and sell stocks, play action cards, and navigate market events. Build your portfolio, manipulate prices, and become the wealthiest player!\n\nJoin at: ${window.location.origin}?room=${encodeURIComponent(room)}`}
              tooltip="Copy room link"
            />
            <NativeShareButton
              title={`Join room ${room} - Pump N Dump`}
              text={`Hey! Come join me in room "${room}" for a game of Pump N Dump! 🎮\n\nA strategic card game where players buy and sell stocks, play action cards, and navigate market events. Build your portfolio, manipulate prices, and become the wealthiest player!\n\nJoin at: ${window.location.origin}?room=${encodeURIComponent(room)}`}
              url={`${window.location.origin}?room=${encodeURIComponent(room)}`}
              tooltip="Share room link"
            />
          </div>
          <div className="room-badge">
            <span className="badge-label">Player name:</span>
            <span className="badge-value">{name}</span>
          </div>
          <div className="room-badge" style={{ cursor: 'pointer' }} onClick={() => setRulesDialogOpen(true)}>
            <span className="badge-label">📖 Game Manual</span>
          </div>
        </div>

        {/* Players Section */}
        <div className="lobby-card players-card">
          <div className="card-header">
            <h2>👥 Players</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {isFirstPlayer && (
                <div style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(255, 193, 7, 0.15)',
                  color: '#f57c00',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                }}>
                  👑 Host
                </div>
              )}
              <div className="player-count-badge">
                {playerCount} / 8 Players
              </div>
            </div>
          </div>
          <div className="card-content">
            <div className="players-grid">
              {slots.map((slot, index) => {
                if (slot.type === 'empty') {
                  // Empty slot - show (+) button for first player
                  return (
                    <div
                      key={`empty-${index}`}
                      className="player-slot empty-slot"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '140px',
                        border: '2px dashed #ddd',
                        borderRadius: '12px',
                        backgroundColor: '#fafafa',
                        padding: '16px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (isFirstPlayer) {
                          e.currentTarget.style.borderColor = 'rgba(76, 175, 80, 0.5)';
                          e.currentTarget.style.backgroundColor = 'rgba(76, 175, 80, 0.02)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (isFirstPlayer) {
                          e.currentTarget.style.borderColor = '#ddd';
                          e.currentTarget.style.backgroundColor = '#fafafa';
                        }
                      }}
                    >
                      {isFirstPlayer ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', width: '100%' }}>
                          <div style={{ 
                            fontSize: '3rem', 
                            opacity: 0.3,
                            marginBottom: '4px'
                          }}>
                            👤
                          </div>
                          <span style={{ fontSize: '0.8rem', color: '#999', fontWeight: '500' }}>Empty Slot</span>
                          <FormControl size="small" style={{ minWidth: '140px', width: '100%' }}>
                            <Select
                              value=""
                              displayEmpty
                              open={addBotSlotIndex === index}
                              onOpen={() => setAddBotSlotIndex(index)}
                              onClose={() => setAddBotSlotIndex(null)}
                              onChange={(e) => {
                                onAddBot(e.target.value === 'RANDOM' ? null : e.target.value, index);
                                setAddBotSlotIndex(null);
                              }}
                              renderValue={() => (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '1.2rem' }}>+</span>
                                  <span style={{ fontSize: '0.85rem', color: '#4CAF50', fontWeight: 'bold' }}>Add Bot</span>
                                </div>
                              )}
                              style={{
                                border: 'none',
                                borderBottom: 'none',
                                backgroundColor: 'rgba(76, 175, 80, 0.05)',
                                borderRadius: '8px'
                              }}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    border: 'none',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                  }
                                }
                              }}
                            >
                              {BOT_PERSONALITIES.map((personality) => (
                                <MenuItem key={personality.value} value={personality.value}>
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span>{personality.label}</span>
                                    <span style={{ fontSize: '0.7rem', color: '#666' }}>
                                      {personality.description}
                                    </span>
                                  </div>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <span style={{ fontSize: '0.7rem', color: '#999', textAlign: 'center', lineHeight: '1.3' }}>
                            Click to add a bot player
                          </span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          <div style={{ 
                            fontSize: '2.5rem', 
                            opacity: 0.2,
                            marginBottom: '4px'
                          }}>
                            👤
                          </div>
                          <span style={{ fontSize: '0.75rem', color: '#ccc' }}>Empty</span>
                        </div>
                      )}
                    </div>
                  );
                } else {
                  // Player slot
                  const playerName = slot.playerName;
                  const isBot = playerEmojis[playerName] === '🤖';
                  const botPersonality = isBot ? (botPersonalities[playerName] || null) : null;
                  
                  const isHost = validPlayerNames[0] === playerName;
                  
                  return (
                    <div
                      key={`player-${index}`}
                      className="player-slot"
                      onClick={playerName === name ? () => setCustomizeDialogOpen(true) : undefined}
                      style={{
                        cursor: playerName === name ? 'pointer' : 'default',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        position: 'relative',
                        border: playerName === name 
                          ? '2px solid rgba(76, 175, 80, 0.5)' 
                          : isHost 
                            ? '2px solid rgba(255, 193, 7, 0.3)'
                            : '2px solid #e0e0e0',
                        borderRadius: '12px',
                        padding: '16px',
                        minHeight: '140px',
                        boxSizing: 'border-box',
                        background: playerName === name 
                          ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(76, 175, 80, 0.02) 100%)'
                          : isHost
                            ? 'linear-gradient(135deg, rgba(255, 193, 7, 0.05) 0%, rgba(255, 193, 7, 0.02) 100%)'
                            : 'white'
                      }}
                      onMouseEnter={(e) => {
                        if (playerName === name) {
                          e.currentTarget.style.transform = 'scale(1.03)';
                          e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (playerName === name) {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      {/* Host Badge */}
                      {isHost && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          fontSize: '1rem'
                        }}>
                          👑
                        </div>
                      )}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        flexShrink: 0,
                        overflow: 'visible',
                        position: 'relative'
                      }}>
                        <PlayerAvatar
                          playerName={playerName}
                          playerColor={playerColors[playerName]}
                          playerEmoji={playerEmojis[playerName]}
                          isReady={playerReady[playerName] === true}
                          showReadyIndicator={true}
                          size="medium"
                          showLabel={false}
                        />
                      </div>
                      
                      {/* Player Name */}
                      <div style={{
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        color: '#2d3748',
                        textAlign: 'center',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {playerName}
                        {playerName === name && (
                          <span style={{ fontSize: '0.7rem', color: '#666', display: 'block', fontWeight: 'normal', marginTop: '2px' }}>
                            (You)
                          </span>
                        )}
                      </div>
                      
                      {/* Ready Status Badge */}
                      <div style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        backgroundColor: playerReady[playerName] === true ? 'rgba(76, 175, 80, 0.15)' : 'rgba(255, 193, 7, 0.15)',
                        color: playerReady[playerName] === true ? '#4CAF50' : '#f57c00',
                        border: `1px solid ${playerReady[playerName] === true ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`,
                      }}>
                        {playerReady[playerName] === true ? '✓ Ready' : '⏳ Waiting'}
                      </div>
                      
                      {playerName === name && (
                        <div className="avatar-hint">
                          Click to customize avatar
                        </div>
                      )}
                      {isBot && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', width: '100%' }}>
                          {editingBotPersonality === playerName ? (
                            <FormControl size="small" style={{ minWidth: '120px', width: '100%' }}>
                              <Select
                                value={botPersonality ? getPersonalityValue(botPersonality) : 'RANDOM'}
                                onChange={(e) => {
                                  if (onChangeBotPersonality) {
                                    onChangeBotPersonality(playerName, e.target.value);
                                    setEditingBotPersonality(null);
                                  }
                                }}
                                onBlur={() => setEditingBotPersonality(null)}
                                autoFocus
                              >
                                {BOT_PERSONALITIES.map((personality) => (
                                  <MenuItem key={personality.value} value={personality.value}>
                                    {personality.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isFirstPlayer) {
                                  setEditingBotPersonality(playerName);
                                }
                              }}
                              style={{
                                fontSize: '0.8rem',
                                color: '#666',
                                cursor: isFirstPlayer ? 'pointer' : 'default',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                border: '1px solid rgba(33, 150, 243, 0.2)',
                                textAlign: 'center',
                                minWidth: '110px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease'
                              }}
                              title={isFirstPlayer ? "Click to change bot personality" : undefined}
                              onMouseEnter={(e) => {
                                if (isFirstPlayer) {
                                  e.currentTarget.style.backgroundColor = 'rgba(33, 150, 243, 0.15)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (isFirstPlayer) {
                                  e.currentTarget.style.backgroundColor = 'rgba(33, 150, 243, 0.1)';
                                }
                              }}
                            >
                              {botPersonality ? getPersonalityLabel(botPersonality) : '🎲 Random'}
                            </div>
                          )}
                          {isFirstPlayer && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onRemoveBot) {
                                  onRemoveBot(playerName);
                                }
                              }}
                              style={{
                                fontSize: '0.7rem',
                                padding: '4px 12px',
                                minWidth: 'auto',
                                textTransform: 'none'
                              }}
                            >
                              🗑️ Remove
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }
              })}
            </div>
            
            {playerCount < 2 && (
              <div className="info-message">
                <span className="info-icon">ℹ️</span>
                <span>Need at least 2 players to start. {isFirstPlayer && 'Add bots by clicking empty slots!'}</span>
              </div>
            )}
            
            {/* Quick Start Guide for new lobbies */}
            {isFirstPlayer && (
              <div style={{
                marginTop: '16px',
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
                borderRadius: '12px',
                border: '2px solid rgba(76, 175, 80, 0.2)',
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#2d3748', fontSize: '0.9rem' }}>
                  🎮 Quick Start Guide
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666', lineHeight: '1.6' }}>
                  1. Share the room code with friends or add bots<br/>
                  2. Customize your avatar by clicking on it<br/>
                  3. When ready, click "I'm Ready!" below<br/>
                  4. Once all players are ready, start the game!
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Countdown Warning */}
        {countdown !== null && countdown > 0 && (
          <div className="lobby-card countdown-card">
            <div className="countdown-content">
              <div className="countdown-number">
                ⚠️ {countdown}
              </div>
              <div className="countdown-message">
                Game starting in {countdown} second{countdown !== 1 ? 's' : ''}!
              </div>
              <div className="countdown-hint">
                Get ready!
              </div>
            </div>
          </div>
        )}


        {/* Game Options Section */}
        <GameOptions
          gameOptions={gameOptions}
          isHost={isFirstPlayer}
          onUpdateOption={onUpdateGameOption}
        />

        {/* Chat Section */}
        <div className="lobby-card chat-card">
          <div 
            className="card-header" 
            style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            onClick={() => setChatExpanded(!chatExpanded)}
          >
            <h2>💬 Chat</h2>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setChatExpanded(!chatExpanded);
              }}
              style={{ padding: '4px' }}
            >
              {chatExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>
          <Collapse in={chatExpanded}>
            <LobbyChat
              socket={socket}
              room={room}
              name={name}
              playerColors={playerColors}
              playerEmojis={playerEmojis}
              messages={chatMessages}
            />
          </Collapse>
        </div>

     



        {/* Buttons Container */}
        <div className="lobby-buttons-container">
          {/* Status Messages */}
          {playerCount >= 2 && !allPlayersReady && countdown === null && (
            <div style={{
              padding: '16px',
              background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 193, 7, 0.1) 100%)',
              borderRadius: '12px',
              border: '2px solid rgba(255, 193, 7, 0.3)',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>⏳</div>
              <div style={{ fontWeight: 'bold', color: '#2d3748', marginBottom: '4px' }}>
                Waiting for players...
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666' }}>
                {validPlayerNames.filter(n => !playerReady[n]).length} player(s) not ready yet
              </div>
            </div>
          )}
          
          {allPlayersReady && isFirstPlayer && countdown === null && (
            <div style={{
              padding: '16px',
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.1) 100%)',
              borderRadius: '12px',
              border: '2px solid rgba(76, 175, 80, 0.3)',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>✓</div>
              <div style={{ fontWeight: 'bold', color: '#2d3748', marginBottom: '4px' }}>
                All players ready!
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666' }}>
                Click "Start Game" below to begin
              </div>
            </div>
          )}
          
          {/* Ready Button */}
          <div className="lobby-actions" style={{ marginBottom: '16px', justifyContent: 'center' }}>
            <Button
              color={isReady ? "secondary" : "primary"}
              variant={isReady ? "contained" : "outlined"}
              onClick={onToggleReady}
              className="lobby-button ready-button"
              size="large"
              disabled={countdown !== null}
              style={{
                minWidth: '200px',
                fontSize: '1.1rem',
                padding: '14px 32px'
              }}
            >
              {isReady ? '✓ Ready' : "⏳ I'm Ready!"}
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="lobby-actions">
            {/* Start Game Button - Only show for first player */}
            {isFirstPlayer && (
              <Button
                color="primary"
                variant="contained"
                onClick={onStartGame}
                className="lobby-button start-button"
                size="large"
                disabled={playerCount < 2 || !allPlayersReady || countdown !== null}
                style={{
                  fontSize: '1.1rem',
                  padding: '14px 28px'
                }}
              >
                {countdown !== null ? `⏳ Starting... (${countdown})` : '🚀 Start Game'}
              </Button>
            )}
            
            <Button
              variant="outlined"
              onClick={onLeaveRoom}
              className="lobby-button leave-button"
              size="large"
              disabled={countdown !== null}
            >
              🚪 Leave Room
            </Button>
          </div>
          
          {/* Helper text for non-hosts */}
          {!isFirstPlayer && playerCount >= 2 && (
            <div style={{
              marginTop: '16px',
              textAlign: 'center',
              fontSize: '0.85rem',
              color: '#999'
            }}>
              The host will start the game when all players are ready
            </div>
          )}
          
         
        </div>
      </div>

      <CustomizeAvatarDialog
        open={customizeDialogOpen}
        onClose={() => setCustomizeDialogOpen(false)}
        name={name}
        playerColor={playerColors[name]}
        playerEmoji={playerEmojis[name]}
        isReady={isReady}
        availableColors={availableColors}
        availableEmojis={availableEmojis}
        onChangePlayerColor={onChangePlayerColor}
        onChangePlayerEmoji={onChangePlayerEmoji}
        onRandomizePlayerColor={onRandomizePlayerColor}
        onRandomizePlayerEmoji={onRandomizePlayerEmoji}
        onRandomizeAll={handleRandomizeAll}
      />

      <GameRulesDialog
        open={rulesDialogOpen}
        onClose={() => setRulesDialogOpen(false)}
      />
    </div>
  );
};

export default Lobby;

